// BulletinBoardTab.jsx — Chapter message board, extracted from
// LimitedPartnerPortal. Pinned + regular messages with emoji reactions,
// pin/unpin and delete controls, and the compose box. Message state
// (bulletinMessages, the compose draft, and lastViewedTimestamp) stays in the
// parent so the draft and unread count survive tab switches; the Firestore
// writes for post / delete / pin / react live here because only this tab
// uses them, with loadBulletinMessages passed in to refresh after each write.
import React from "react";
import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDoc, Timestamp
} from "firebase/firestore";
import { db } from "../../../firebaseConfig.js";
import { notify } from "../../ui/Toast";

// Same thin wrappers the portal uses for alerts/confirms.
const showAppAlert = (message, options) => {
  notify(message, options);
};

const showAppConfirm = (message) => {
  return window.confirm(message);
};

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Unknown time';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Reactions component for bulletin board messages
const MessageReactions = ({ message, user, handleReactToMessage, chapterMembers }) => {
  const availableEmojis = ['👍', '💖', '🎉', '🤔', '😊', '👀'];
  const reactions = message.reactions || {};

  return (
    <div style={{
      marginTop: '15px',
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      {/* Display existing reactions */}
      {Object.entries(reactions).map(([emoji, users]) => {
        if (users.length === 0) return null;
        const hasReacted = users.includes(user.uid);

        return (
          <button
            key={emoji}
            type="button"
            className="win95-btn win95-btn--compact"
            onClick={() => handleReactToMessage(message.id, emoji)}
            aria-pressed={hasReacted}
            style={{
              background: hasReacted ? 'var(--mb-magenta-soft)' : undefined,
              fontSize: '14px',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector('.reaction-tooltip');
              if (tooltip) tooltip.style.display = 'block';
            }}
            onMouseLeave={(e) => {
              const tooltip = e.currentTarget.querySelector('.reaction-tooltip');
              if (tooltip) tooltip.style.display = 'none';
            }}
          >
            <span>{emoji}</span>
            <span style={{ fontSize: '12px', fontFamily: 'var(--font-numeral)', color: 'var(--mb-ink-60)' }}>{users.length}</span>

            {/* Tooltip showing who reacted */}
            <div
              className="reaction-tooltip"
              style={{
                display: 'none',
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--mb-ink)',
                color: 'var(--mb-chalk)',
                padding: '6px 10px',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: '4px',
                zIndex: 1000,
                maxWidth: '200px',
                textAlign: 'center'
              }}
            >
              {users.slice(0, 3).map((uid, idx) => {
                const member = chapterMembers.find(m => m.uid === uid);
                return member?.name || member?.email || 'Unknown';
              }).join(', ')}
              {users.length > 3 && ` and ${users.length - 3} more`}
            </div>
          </button>
        );
      })}

      {/* Add reaction button */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          className="win95-btn win95-btn--compact"
          onClick={(e) => {
            const btn = e.currentTarget;
            const picker = btn.nextElementSibling;
            picker.style.display = picker.style.display === 'none' ? 'block' : 'none';
          }}
          title="Add reaction"
          aria-label="Add reaction"
        >
          +
        </button>

        {/* Emoji picker dropdown */}
        <div style={{
          display: 'none',
          position: 'absolute',
          bottom: '100%',
          left: '0',
          background: 'var(--chrome-face)',
          border: '2px solid',
          borderColor: 'var(--bevel-raised)',
          boxShadow: 'var(--shadow-raised)',
          padding: '8px',
          marginBottom: '4px',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {availableEmojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => {
                  handleReactToMessage(message.id, emoji);
                  // Hide picker
                  const picker = document.currentScript?.parentElement;
                  if (picker) picker.style.display = 'none';
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--chrome-light)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function BulletinBoardTab({
  user,
  isAdmin,
  chapterMembers,
  bulletinMessages,
  newBulletinMessage,
  setNewBulletinMessage,
  lastViewedTimestamp,
  loadBulletinMessages,
}) {

// Bulletin board functions
const handleCreateBulletinMessage = async () => {
  if (!newBulletinMessage.trim()) {
    showAppAlert("Please enter a message.");
    return;
  }

  try {
    const messageData = {
      message: newBulletinMessage.trim(),
      authorId: user.uid,
      authorName: user.name || user.email,
      authorChapter: user.chapter,
      authorRole: user.role,
      timestamp: Timestamp.now(),
      isPinned: false,
      pinnedBy: null,
      pinnedAt: null
    };

    await addDoc(collection(db, "bulletinMessages"), messageData);

    // Clear form
    setNewBulletinMessage('');

    // Reload messages
    await loadBulletinMessages();

  } catch (error) {
    console.error("LPPortal: Error creating bulletin message:", error);
    showAppAlert("Failed to post message. Please try again.");
  }
};

const handleDeleteBulletinMessage = async (messageId) => {
  if (showAppConfirm("Are you sure you want to delete this message?")) {
    try {
      await deleteDoc(doc(db, "bulletinMessages", messageId));
      loadBulletinMessages();
    } catch (error) {
      console.error("LPPortal: Error deleting bulletin message:", error);
      showAppAlert("Failed to delete message.");
    }
  }
};

const handleTogglePinMessage = async (message) => {
  try {
    const updates = {
      isPinned: !message.isPinned,
      pinnedBy: !message.isPinned ? user.uid : null,
      pinnedAt: !message.isPinned ? Timestamp.now() : null
    };

    await updateDoc(doc(db, "bulletinMessages", message.id), updates);
    loadBulletinMessages();
  } catch (error) {
    console.error("LPPortal: Error toggling pin:", error);
    showAppAlert("Failed to update message pin status.");
  }
};

const handleReactToMessage = async (messageId, emoji) => {
  try {
    const messageRef = doc(db, "bulletinMessages", messageId);
    const messageDoc = await getDoc(messageRef);

    if (!messageDoc.exists()) {
      console.error("LPPortal: Message not found");
      return;
    }

    const messageData = messageDoc.data();
    const reactions = messageData.reactions || {};
    const emojiReactions = reactions[emoji] || [];

    // Toggle reaction
    let updatedReactions;
    if (emojiReactions.includes(user.uid)) {
      // Remove reaction
      updatedReactions = emojiReactions.filter(uid => uid !== user.uid);
    } else {
      // Add reaction
      updatedReactions = [...emojiReactions, user.uid];
    }

    // Update the reactions object
    const updatedReactionsObj = {
      ...reactions,
      [emoji]: updatedReactions
    };

    // Remove emoji key if no reactions left
    if (updatedReactions.length === 0) {
      delete updatedReactionsObj[emoji];
    }

    await updateDoc(messageRef, {
      reactions: updatedReactionsObj
    });

    loadBulletinMessages();
  } catch (error) {
    console.error("LPPortal: Error toggling reaction:", error);
    showAppAlert("Failed to update reaction.");
  }
};

return (
        <div style={{ height: '100%', overflow: 'hidden' }}>
          <style>{`
            @media (max-width: 768px) {
              .bulletin-container {
                flex-direction: column !important;
              }
              .bulletin-messages {
                order: 2;
                height: 60vh !important;
                border-right: none !important;
                border-bottom: 1px solid var(--gnf-bg-silver);
              }
              .bulletin-compose {
                order: 1;
                width: 100% !important;
                height: auto !important;
                border-bottom: 1px solid var(--gnf-bg-silver);
                padding: 15px !important;
              }
            }
          `}</style>

          <div className="bulletin-container" style={{
            display: 'flex',
            height: '100%',
            background: 'var(--mb-paper)'
          }}>
            {/* Messages List - Left Side */}
            <div className="bulletin-messages" style={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--gnf-bg)',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">{user?.chapter || 'All chapters'}</div>
                  <h2 className="win95-pane-header__title">Message board</h2>
                </div>
              </div>

              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px'
              }}>
            {bulletinMessages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: 'var(--mb-ink-60)',
                fontSize: '14px'
              }}>
                No messages yet. Be the first to post!
              </div>
            ) : (
              <>
                {/* Pinned Messages */}
                {bulletinMessages.filter(msg => msg.isPinned).map(message => (
                  <div
                    key={message.id}
                    style={{
                      background: 'var(--mb-butter-soft)',
                      border: '2px solid var(--mb-ink)',
                      padding: '15px',
                      marginBottom: '15px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '16px' }}>Pinned</span>
                        </div>
                        <p style={{
                          margin: '10px 0',
                          fontSize: '14px',
                          color: 'var(--mb-ink)',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5'
                        }}>
                          {message.message}
                        </p>
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--mb-ink-60)',
                          display: 'flex',
                          gap: '15px',
                          alignItems: 'center'
                        }}>
                          <span>{message.authorName}</span>
                          <span>•</span>
                          <span>{formatRelativeTime(message.timestamp)}</span>
                        </div>

                        {/* Reactions */}
                        <MessageReactions
                          message={message}
                          user={user}
                          handleReactToMessage={handleReactToMessage}
                          chapterMembers={chapterMembers}
                        />
                      </div>

                      {/* Admin Actions */}
                      {(isAdmin || message.authorId === user.uid) && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {isAdmin && (
                            <button
                              type="button"
                              className="win95-btn win95-btn--compact"
                              onClick={() => handleTogglePinMessage(message)}
                              title="Unpin message"
                            >
                              Unpin
                            </button>
                          )}
                          <button
                            type="button"
                            className="win95-btn win95-btn--compact win95-btn-danger"
                            onClick={() => handleDeleteBulletinMessage(message.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Regular Messages */}
                {bulletinMessages.filter(msg => !msg.isPinned).map(message => {
                  const msgDate = message.timestamp?.toDate ? message.timestamp.toDate() : new Date(message.timestamp);
                  const isNew = msgDate > new Date(lastViewedTimestamp) && message.authorId !== user?.uid;

                  return (
                    <div
                      key={message.id}
                      style={{
                        background: isNew ? 'var(--mb-aqua-soft)' : 'var(--gnf-bg)',
                        border: isNew ? '2px solid var(--mb-aqua)' : '1px solid var(--gnf-bg-silver)',
                        padding: '15px',
                        marginBottom: '15px',
                        position: 'relative'
                      }}
                    >
                    {isNew && (
                      <span style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'var(--mb-aqua-deep)',
                        color: 'var(--mb-chalk)',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        New
                      </span>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          margin: '10px 0',
                          fontSize: '14px',
                          color: 'var(--mb-ink)',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {message.message}
                        </p>
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--mb-ink-60)',
                          display: 'flex',
                          gap: '15px',
                          alignItems: 'center'
                        }}>
                          <span>{message.authorName}</span>
                          <span>•</span>
                          <span>{formatRelativeTime(message.timestamp)}</span>
                        </div>

                        {/* Reactions */}
                        <MessageReactions
                          message={message}
                          user={user}
                          handleReactToMessage={handleReactToMessage}
                          chapterMembers={chapterMembers}
                        />
                      </div>

                      {/* Admin Actions */}
                      {(isAdmin || message.authorId === user.uid) && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {isAdmin && (
                            <button
                              type="button"
                              className="win95-btn win95-btn--compact"
                              onClick={() => handleTogglePinMessage(message)}
                              title="Pin message"
                            >
                              Pin
                            </button>
                          )}
                          <button
                            type="button"
                            className="win95-btn win95-btn--compact win95-btn-danger"
                            onClick={() => handleDeleteBulletinMessage(message.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Message Compose Box - Bottom of messages */}
          <div className="bulletin-compose" style={{
            background: 'var(--chrome-face)',
            borderTop: '2px solid var(--chrome-shade)',
            boxShadow: 'inset 0 1px 0 var(--chrome-highlight)',
            padding: '15px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <textarea
              placeholder="Write your message…"
              value={newBulletinMessage}
              onChange={(e) => setNewBulletinMessage(e.target.value)}
              style={{
                minHeight: '60px',
                maxHeight: '120px',
                padding: '10px',
                border: '2px solid',
                borderColor: 'var(--bevel-sunken)',
                background: 'var(--gnf-bg)',
                boxShadow: 'none',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="win95-btn win95-btn-primary"
                onClick={handleCreateBulletinMessage}
              >
                Post message
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
);
}
